// src/components/Admin/tabs/AvatarsTab.jsx
import React, { useState } from "react";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Map,
  Copy,
} from "lucide-react";
import MapConfigEditor, { GEO_DEFAULTS } from "../shared/MapConfigEditor";
import { DEFAULT_TIERS } from "../shared/TierEditor";

const AvatarsTab = ({
  avatars,
  maps,
  states,
  onAvatarsChange,
  onMapsChange,
}) => {
  // Track which maps are expanded
  const [expandedMaps, setExpandedMaps] = useState({});

  const toggleMapExpanded = (mapId) => {
    setExpandedMaps((prev) => ({ ...prev, [mapId]: !prev[mapId] }));
  };

  // ---- Avatar helpers ----
  const addAvatar = () => {
    const newAvatar = {
      id: "",
      name: "",
      description: "",
      reports: {},
    };
    onAvatarsChange([...avatars, newAvatar]);
  };

  const updateAvatar = (idx, patch) => {
    onAvatarsChange(
      avatars.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    );
  };

  const removeAvatar = (idx) => {
    const avatarId = avatars[idx]?.id;
    // Also remove any maps linked to this avatar
    if (avatarId) {
      onMapsChange(maps.filter((m) => m.avatarId !== avatarId));
    }
    onAvatarsChange(avatars.filter((_, i) => i !== idx));
  };

  // ---- Map helpers ----
  const addMap = (avatarId) => {
    const newMap = {
      id: "",
      label: "",
      avatarId,
      stateAbbreviation: states[0]?.abbreviation || "",
      geographyType: "",
      geographyLabel: "",
      geographyLabelPlural: "",
      data: {
        scoredDataPath: "",
        topoJsonObjectName: "zcta",
      },
      display: {},
      popup: { titleField: "city", subtitleField: "", fields: [] },
      details: { sections: [] },
      tierCutoffs: { ...DEFAULT_TIERS },
      reports: {},
      default: maps.length === 0, // first map is default
    };
    onMapsChange([...maps, newMap]);

    // Auto-expand the new map (it'll be at the end of the maps array)
    const newMapIdx = maps.length;
    setExpandedMaps((prev) => ({ ...prev, [`map_${newMapIdx}`]: true }));
  };

  const updateMap = (mapIdx, patch) => {
    onMapsChange(maps.map((m, i) => (i === mapIdx ? { ...m, ...patch } : m)));
  };

  const removeMap = (mapIdx) => {
    onMapsChange(maps.filter((_, i) => i !== mapIdx));
  };

  const duplicateMap = (mapIdx) => {
    const source = maps[mapIdx];
    const copy = {
      ...JSON.parse(JSON.stringify(source)),
      id: `${source.id}_copy`,
      label: `${source.label} (Copy)`,
      default: false,
    };
    onMapsChange([...maps, copy]);
  };

  // Get maps for a specific avatar
  const getMapsForAvatar = (avatarId) => {
    return maps
      .map((m, idx) => ({ ...m, _idx: idx }))
      .filter((m) => m.avatarId === avatarId);
  };

  // Auto-generate avatar ID from name
  const suggestAvatarId = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, 30);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            Avatars & Maps
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Define business models (avatars) and configure maps for each
          </p>
        </div>
        <button
          type="button"
          onClick={addAvatar}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-secondary hover:bg-secondary/10 border border-secondary/30 rounded-lg transition"
        >
          <Plus size={14} />
          Add Avatar
        </button>
      </div>

      {avatars.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-400">
            No avatars defined. Add an avatar (business model) to start
            configuring maps.
          </p>
        </div>
      )}

      {/* Avatar list */}
      {avatars.map((avatar, avatarIdx) => {
        const avatarMaps = getMapsForAvatar(avatar.id);

        return (
          <div
            key={avatarIdx}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            {/* Avatar header */}
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Avatar ID */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Avatar ID *
                    </label>
                    <input
                      type="text"
                      value={avatar.id || ""}
                      onChange={(e) => {
                        const oldId = avatar.id;
                        const newId = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_]/g, "");
                        updateAvatar(avatarIdx, { id: newId });
                        // Update all maps referencing this avatar
                        if (oldId && oldId !== newId) {
                          onMapsChange(
                            maps.map((m) =>
                              m.avatarId === oldId
                                ? { ...m, avatarId: newId }
                                : m,
                            ),
                          );
                        }
                      }}
                      placeholder="self_service"
                      className="w-full px-2.5 py-1.5 text-sm font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>

                  {/* Avatar name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={avatar.name || ""}
                      onChange={(e) => {
                        const name = e.target.value;
                        const patch = { name };
                        // Auto-suggest ID if ID is empty
                        if (!avatar.id) {
                          patch.id = suggestAvatarId(name);
                        }
                        updateAvatar(avatarIdx, patch);
                      }}
                      placeholder="Self-Service Laundromat"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={avatar.description || ""}
                      onChange={(e) =>
                        updateAvatar(avatarIdx, {
                          description: e.target.value,
                        })
                      }
                      placeholder="Target demographic description"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removeAvatar(avatarIdx)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition mt-5"
                  title="Remove avatar and its maps"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Methodology report config */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Methodology Report
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={avatar.reports?.methodology?.title || ""}
                    onChange={(e) =>
                      updateAvatar(avatarIdx, {
                        reports: {
                          ...avatar.reports,
                          methodology: {
                            ...(avatar.reports?.methodology || {}),
                            title: e.target.value,
                            icon: "book-open",
                          },
                        },
                      })
                    }
                    placeholder="Methodology title"
                    className="px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                  <input
                    type="text"
                    value={avatar.reports?.methodology?.fileName || ""}
                    onChange={(e) =>
                      updateAvatar(avatarIdx, {
                        reports: {
                          ...avatar.reports,
                          methodology: {
                            ...(avatar.reports?.methodology || {}),
                            fileName: e.target.value,
                          },
                        },
                      })
                    }
                    placeholder="filename.pdf"
                    className="px-2.5 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                  <input
                    type="text"
                    value={avatar.reports?.methodology?.storagePath || ""}
                    onChange={(e) =>
                      updateAvatar(avatarIdx, {
                        reports: {
                          ...avatar.reports,
                          methodology: {
                            ...(avatar.reports?.methodology || {}),
                            storagePath: e.target.value,
                          },
                        },
                      })
                    }
                    placeholder="reports/methodology.pdf"
                    className="px-2.5 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
              </div>
            </div>

            {/* Maps for this avatar */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-600">
                  Maps ({avatarMaps.length})
                </span>
                <button
                  type="button"
                  onClick={() => addMap(avatar.id)}
                  disabled={!avatar.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={12} />
                  Add Map
                </button>
              </div>

              {avatarMaps.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">
                  {avatar.id
                    ? "No maps yet. Add a map to configure geographies for this avatar."
                    : "Set an avatar ID first, then add maps."}
                </p>
              )}

              <div className="space-y-3">
                {avatarMaps.map((m) => {
                  const mapKey = `map_${m._idx}`;
                  const isExpanded = expandedMaps[mapKey];

                  return (
                    <div
                      key={mapKey}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Map header (always visible) */}
                      <div
                        className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                        onClick={() => toggleMapExpanded(mapKey)}
                      >
                        {isExpanded ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                        <Map size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 flex-1">
                          {m.label || m.id || "New Map"}
                          {m.stateAbbreviation && m.geographyType && (
                            <span className="ml-2 text-xs text-gray-400 font-normal">
                              {m.stateAbbreviation} · {m.geographyType}
                            </span>
                          )}
                          {m.default && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-secondary/10 text-secondary rounded">
                              default
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateMap(m._idx);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                            title="Duplicate map"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMap(m._idx);
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 rounded"
                            title="Remove map"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Map config (expanded) */}
                      {isExpanded && (
                        <div className="p-4 border-t border-gray-200">
                          <MapConfigEditor
                            map={m}
                            states={states}
                            avatars={avatars}
                            onChange={(patch) => updateMap(m._idx, patch)}
                            onRemove={() => removeMap(m._idx)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AvatarsTab;
